"use client";

import { useEffect, useState } from "react";
import { getPublicSettings } from "@/lib/api";
import type { PublicSettings } from "@/lib/types";
import { NAV_ITEMS, type NavItem } from "@/components/shell/nav";

/** Module-level so every consumer shares one settings fetch per page load. */
let cached: Promise<PublicSettings> | null = null;

function fetchPublicSettings(): Promise<PublicSettings> {
  cached ??= getPublicSettings().catch((error) => {
    cached = null; // allow a retry on the next mount
    throw error;
  });
  return cached;
}

/**
 * Shared, memoized public app settings (one fetch per page). Returns null until
 * loaded — feature-flag consumers should treat that as "enabled" so a valid
 * surface never flickers away while the fetch is in flight.
 */
export function usePublicSettings(): PublicSettings | null {
  const [settings, setSettings] = useState<PublicSettings | null>(null);
  useEffect(() => {
    let mounted = true;
    fetchPublicSettings()
      .then((s) => {
        if (mounted) setSettings(s);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);
  return settings;
}

/** Primary nav items with feature-flagged surfaces removed (Browse when off). */
export function useNavItems(): NavItem[] {
  const settings = usePublicSettings();
  if (settings?.browseEnabled === false) {
    return NAV_ITEMS.filter((item) => item.href !== "/browse");
  }
  return NAV_ITEMS;
}
